// @ts-check
// Seção da retenção na página da família (contrato 10 de atendimento, seção 24.5): pontuação, risco, sinais com origem e
// "Dispensar", "Marcar risco" e "Abrir caso" (ou o link do caso aberto) e o aviso da Lei 9.870/1999. Montada pela F0 em
// record-show-page.js e record-panel.js; some fora da família, com o módulo desligado e para quem não tem o atendimento.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { hasCurrentPermissionFlag, isTenantModuleEnabled } from '../../data/metadata-store.js';
import { fetchHouseholdRetention } from '../../data/retention.js';
import { RISK_COLORS, sortSignals } from '../../lib/retention-display.js';
import { formatDate, formatDateTime } from '../../lib/format.js';
import { Link } from '../../app/router.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { t } from '../../i18n/index.js';
import { RetentionDialogHost, openRetentionDialog } from './retention-dialogs.js';

/** @typedef {import('../../data/retention.js').HouseholdRetention} HouseholdRetention */
/** @typedef {import('../../data/retention.js').RetentionSignal} RetentionSignal */

/** Etiqueta do risco com a pontuação. @param {{ risk: string | null, score: number }} props */
export function RetentionRiskTag({ risk, score }) {
  return html`<span class="retention-risk" data-risk=${risk ?? 'NONE'}>
    <${Tag} color=${RISK_COLORS[risk ?? ''] ?? 'gray'} text=${t(`retention.risks.${risk ?? 'NONE'}`)} />
    <span class="retention-score">${t('retention.points', { count: score })}</span>
  </span>`;
}

/** Origem do sinal: o atendimento (link quando o papel o vê), a matrícula, a família ou a observação da equipe. @param {{ signal: RetentionSignal }} props */
export function RetentionSignalSource({ signal }) {
  const kind = t(`retention.sourceKinds.${signal.sourceKind}`);
  if (signal.sourceKind === 'TICKET' && !signal.sourceLabel) return html`<span class="retention-signal-source">${kind} · ${t('retention.page.hiddenSource')}</span>`;
  if (signal.sourceTicketId) {
    return html`<span class="retention-signal-source">${kind} · <${Link} href=${`/object/serviceTicket/${signal.sourceTicketId}`}>${signal.sourceLabel}<//></span>`;
  }
  return html`<span class="retention-signal-source">${signal.sourceLabel ? `${kind} · ${signal.sourceLabel}` : kind}</span>`;
}

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: Record<string, any>, onChanged?: () => void }} props
 */
export function HouseholdRetentionSection({ objectMetadata, record, onChanged }) {
  const isHousehold = objectMetadata?.nameSingular === 'household';
  const isVisible = isHousehold && isTenantModuleEnabled('SERVICE_DESK') && hasCurrentPermissionFlag('SERVICE_DESK');
  const [state, setState] = useState(/** @type {HouseholdRetention | null | 'hidden'} */ (null));
  const [showInactive, setShowInactive] = useState(false);
  const load = () => {
    if (!isVisible || !record?.id) return;
    fetchHouseholdRetention(record.id).then(setState).catch((error) => {
      if (!(error?.statusCode === 403 || error?.statusCode === 404)) console.error(error);
      setState('hidden');
    });
  };
  useEffect(load, [record?.id, isVisible, record?.retentionScore]);
  if (!isVisible) return null;
  const host = html`<${RetentionDialogHost} />`;
  if (state === 'hidden') return host;
  if (!state) {
    return html`<section class="retention-section" aria-busy="true"><${Skeleton} width=${180} height=${18} /><${Skeleton} width="100%" height=${40} /></section>${host}`;
  }
  const refresh = () => {
    load();
    onChanged?.();
  };
  const familyName = record.name ?? '';
  const signals = sortSignals(state.signals);
  const active = signals.filter((signal) => signal.isActive);
  const inactive = signals.filter((signal) => !signal.isActive);
  const shown = showInactive ? signals : active;

  return html`<section class="retention-section" data-testid="household-retention-section" aria-labelledby="retention-section-title">
    <div class="retention-section-header">
      <h2 class="retention-section-title" id="retention-section-title"><${Icon} name="IconHeartHandshake" size=${14} />${t('retention.section.title')}</h2>
      <${RetentionRiskTag} risk=${state.risk} score=${state.score} />
    </div>
    ${state.updatedAt ? html`<p class="retention-muted">${t('retention.section.updatedAt', { date: formatDateTime(state.updatedAt) })}</p>` : null}
    ${state.transferNotice ? html`<p class="retention-law-notice" role="note"><${Icon} name="IconInfoCircle" size=${14} />${t('retention.lawNotice')}</p>` : null}
    ${shown.length === 0
      ? html`<p class="retention-muted">${t('retention.section.noSignals')}</p>`
      : html`<ul class="retention-signal-list">
          ${shown.map((signal) => html`<li key=${signal.id} class="retention-signal" data-active=${signal.isActive ? 'true' : 'false'}>
            <div class="retention-signal-main">
              <span class="retention-signal-name">${t(`retention.signals.${signal.signalKey}`)}</span>
              <span class="retention-signal-weight">${signal.isActive ? t('retention.section.weight', { weight: signal.weight }) : t(signal.isDismissed ? 'retention.page.dismissed' : 'retention.page.expired')}</span>
            </div>
            <div class="retention-signal-detail">
              <${RetentionSignalSource} signal=${signal} />
              <span>${formatDate(signal.occurredAt)}${signal.isActive ? ` · ${t('retention.section.expiresAt', { date: formatDate(signal.expiresAt) })}` : ''}</span>
            </div>
            ${signal.isActive && state.canDismiss
              ? html`<${Button} title=${t('retention.section.dismiss')} variant="tertiary" size="small" ariaLabel=${t('retention.section.dismissLabel', { signal: t(`retention.signals.${signal.signalKey}`) })}
                  onClick=${() => openRetentionDialog({ kind: 'dismiss', householdId: record.id, signalId: signal.id, signalLabel: t(`retention.signals.${signal.signalKey}`), onDone: refresh })} />`
              : null}
          </li>`)}
        </ul>`}
    ${inactive.length > 0
      ? html`<button type="button" class="retention-link-button" onClick=${() => setShowInactive(!showInactive)}>
          ${showInactive ? t('retention.section.hideInactive') : t('retention.section.showInactive', { count: inactive.length })}
        </button>`
      : null}
    <div class="retention-section-actions">
      <${Button} title=${t('retention.section.markRisk')} icon="IconAlertTriangle" variant="secondary" size="small"
        onClick=${() => openRetentionDialog({ kind: 'mark', householdId: record.id, householdName: familyName, onDone: refresh })} />
      ${state.openCase
        ? state.openCase.isVisible
          ? html`<${Link} href=${`/object/serviceTicket/${state.openCase.ticketId}`} className="retention-case-link">
              <${Icon} name="IconHeartHandshake" size=${14} />${t('retention.section.viewCase', { protocol: state.openCase.protocol ?? '' })}<//>`
          : html`<span class="retention-muted">${t('retention.section.caseHidden')}</span>`
        : html`<${Button} title=${t('retention.section.openCase')} icon="IconHeartHandshake" variant="secondary" size="small"
            onClick=${() => openRetentionDialog({ kind: 'open', householdId: record.id, householdName: familyName, onDone: refresh })} />`}
    </div>
  </section>${host}`;
}
